export type OptionInputObject<TMeta = Record<string, unknown>> = {
    label?: string;
    value: string;
    optgroup?: string;
    disabled?: boolean;
    selected?: boolean;
    meta?: TMeta;
};

export type OptionInput<TMeta = Record<string, unknown>> = string | OptionInputObject<TMeta>;

export type NormalizedOption<TMeta = Record<string, unknown>> = {
    label: string;
    value: string;
    optgroup?: string;
    disabled?: boolean;
    selected?: boolean;
    meta?: TMeta;
};

export type NormalizedOptionGroup<TMeta = Record<string, unknown>> = {
    key: string;
    label?: string;
    options: NormalizedOption<TMeta>[];
};

export type NormalizedOptionGroups<TMeta = Record<string, unknown>> = NormalizedOptionGroup<TMeta>[];

export type OptionGroupingStrategy<TMeta = Record<string, unknown>> = (options: NormalizedOption<TMeta>[]) => NormalizedOptionGroups<TMeta>;

export type Option<TMeta = Record<string, unknown>> = OptionInput<TMeta>;
export type Options<TMeta = Record<string, unknown>> = Option<TMeta>[];
export type OptionProp<TMeta = Record<string, unknown>> = NormalizedOption<TMeta>;
export type OptionGroup<TMeta = Record<string, unknown>> = NormalizedOptionGroup<TMeta>;
export type OptionsGroup<TMeta = Record<string, unknown>> = NormalizedOptionGroups<TMeta>;

const defaultLabel = (value: string) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

export const normalizeOption = <TMeta = Record<string, unknown>>(option: OptionInput<TMeta>): NormalizedOption<TMeta> => {
    if (typeof option === 'string') {
        return {
            value: option,
            label: defaultLabel(option),
        };
    }

    const label = option.label ?? defaultLabel(option.value);

    return {
        value: option.value,
        optgroup: option.optgroup,
        disabled: option.disabled,
        selected: option.selected,
        label,
        meta: option.meta,
    };
};

export const normalizeOptions = <TMeta = Record<string, unknown>>(options: OptionInput<TMeta>[] = []): NormalizedOption<TMeta>[] => {
    return options.map((option) => normalizeOption<TMeta>(option));
};

const defaultGroupNormalizedOptions = <TMeta = Record<string, unknown>>(options: NormalizedOption<TMeta>[]): NormalizedOptionGroups<TMeta> => {
    const groups: NormalizedOptionGroups<TMeta> = [];
    const groupIndexByKey = new Map<string, number>();

    options.forEach((option, index) => {
        if (option.optgroup) {
            const key = option.optgroup;
            if (!groupIndexByKey.has(key)) {
                groupIndexByKey.set(key, groups.length);
                groups.push({ key, label: key, options: [] });
            }
            const groupIndex = groupIndexByKey.get(key) as number;
            groups[groupIndex].options.push(option);
        } else {
            groups.push({ key: `option-${index}`, options: [option] });
        }
    });

    return groups;
};

export const groupNormalizedOptions = <TMeta = Record<string, unknown>>(
    options: NormalizedOption<TMeta>[],
    strategy: OptionGroupingStrategy<TMeta> = defaultGroupNormalizedOptions
): NormalizedOptionGroups<TMeta> => {
    return strategy(options);
};

export { defaultGroupNormalizedOptions };

export const flattenGroups = <TMeta = Record<string, unknown>>(groups: NormalizedOptionGroups<TMeta>): NormalizedOption<TMeta>[] => {
    return groups.flatMap((group) => group.options);
};

export const normalizeOptionGroup = <TMeta = Record<string, unknown>>(options: Options<TMeta>, strategy?: OptionGroupingStrategy<TMeta>): OptionsGroup<TMeta> => {
    const normalized = normalizeOptions<TMeta>(options);
    return (strategy ?? groupNormalizedOptions<TMeta>)(normalized);
};
